<template>
   <LoadingPage v-if="isLoading"></LoadingPage>

   <ul class="nav nav-tabs mb-3" style="padding-left: 8px; border-bottom: none;">
      <li class="nav-item">
         <a class="nav-link" :class="{ active: activeTab === 'contract' }" href="#" @click.prevent="switchTab('contract')" 
            style="cursor: pointer; font-weight: bold; border-radius: 8px 8px 0 0;">
            Contracts
         </a>
      </li>
      <li class="nav-item">
         <a class="nav-link" :class="{ active: activeTab === 'proposal' }" href="#" @click.prevent="switchTab('proposal')"
            style="cursor: pointer; font-weight: bold; border-radius: 8px 8px 0 0;">
            Proposals
         </a>
      </li>
   </ul>

   <div class="card" style="border-top-left-radius: 0; margin-top: -16px;">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">
            <div class="col-12 col-lg-3 mb-2 mb-lg-0">
               <input v-model.trim="search" type="text" class="form-control form-control-sm"
                  placeholder="🔍 Search block name..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
                  @keyup="onSearch()" />
            </div>

            <div v-if="$store.getters['auth/canEditContentLibrary']" class="col-12 col-lg-auto d-flex mb-2 mb-lg-0">
               <button type="button" class="fw-bold btn btn-info text-light btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem;"
                  @click="isModalOpen = true; onButtonClick('create')">
                  Create {{ activeTab === 'contract' ? 'Contract' : 'Proposal' }} Block
               </button>
            </div>

            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <div class="form-check form-switch d-flex align-items-center">
                     <input v-model="defaultOnly" type="checkbox" class="form-check-input me-2 custom-switch-input" id="defaultOnlySwitch" style="cursor: pointer; width: 2.5em; height: 1.25em;">
                     <label class="form-check-label filterTitle mb-0" for="defaultOnlySwitch" style="cursor: pointer; user-select: none;">Defaults Only</label>
                  </div>
               </div>
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort By:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="sortOption in sortByList" :value="sortOption.value" :key="sortOption.value">
                        {{ sortOption.text }}
                     </option>
                  </select>
               </div>
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Items per page:</span>
                  <select v-model="pagination.pageSize" class="form-select form-select-sm shadow-none filterOptions bordered-select">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>

               <div class="pagination">
                  <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow">
                     <i class="fas fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages" class="pagination-arrow">
                     <i class="fas fa-chevron-right"></i>
                  </button>
               </div>
            </div>
         </div>
      </div>
   </div>

   <div v-if="blocks.length < 1" class="text-center p-5">
      <p>No records to display.</p>
   </div>

   <div v-else class="row row-cols-1 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 mt-3">
      <div v-for="block in blocks" :key="block.id" class="col mb-4">
         <div class="card h-100 block-card" @click="$event.stopPropagation(); onButtonClick('block-tab', block.id)">
            <div class="card-header-color-bar" :style="{ backgroundColor: block.block_hex_color }"></div>
            <div class="card-body d-flex flex-column">
               <h5 class="card-title">{{ block.block_name }}</h5>
               <hr class="my-3">
               <div class="card-content-area">
                  <p class="card-text"><strong>Date Created:</strong> {{ formatDate(block.date_created) }}</p>
                  <p class="card-text"><strong>Block Type:</strong> {{ block.BlockType.display_name }}</p>
                  <p class="card-text"><strong>Block Use:</strong> {{ block.BlockUse.display_name }}</p>
                  <p v-if="block.default_version && block.BlockType.name != 'Testimonial'" class="card-text"><strong>Default Version:</strong> <i class="fa-solid fa-check" style="color:green"></i></p>
                  <div v-if="block.products && block.products.length > 0">
                     <p class="card-text mt-2"><strong>Product Applicable:</strong></p>
                     <div class="label-container">
                        <span v-for="product in block.products" :key="product.uuid" class="label">
                           {{ product.Product.display_name }}
                        </span>
                     </div>
                  </div>
                  
                  <div v-if="block.content_type" class="mt-2">
                     <p class="card-text"><strong>Content Type:</strong></p>
                      <div class="label-container">
                        <span class="label">{{ block.content_type.display_name }}</span>
                     </div>
                  </div>
               </div>

               <div class="mt-auto pt-3">
                  <hr class="my-3">
                  <div class="d-flex justify-content-start align-items-center">
                     <button type="button" class="btn btn-circle-xl btn-circle-info me-2" :title="$store.getters['auth/canEditContentLibrary'] ? 'Edit Block' : 'View Block'" @click="$event.stopPropagation(); onButtonClick('block-tab', block.id)">
                        <i :class="$store.getters['auth/canEditContentLibrary'] ? 'fas fa-pencil-alt' : 'fas fa-eye'"></i>
                     </button>
                     <button type="button" class="btn btn-circle-xl btn-circle-info me-2" title="View in New Tab" @click.stop="openBlockInNewTab(block.id)">
                        <i class="fas fa-external-link-alt"></i>
                     </button>
                     <button v-if="$store.getters['auth/isGlobalSuperAdmin']" type="button" class="btn btn-circle-xl btn-circle-dark" title="Delete Block" @click="$event.stopPropagation(); onButtonClick('delete', block.id)">
                        <i class="fas fa-trash-alt"></i>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>
   </div>

   <!-- Custom BDM-style Modal Overlay -->
   <div v-if="isModalOpen" class="modal-backdrop-custom">
      <div class="modal-content-custom">
         <button @click="closeModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">Add Block</h5>
         <hr>
         <div class="modal-body-custom">
            <BlockForm ref="blockFormRef" :initial-block-use="activeTab" @onDelete="getBlockList()" @onSubmit="getBlockList(); closeModal();" />
         </div>
      </div>
   </div>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import moment from 'moment';
import { debounce, truncateLongText } from '@/plugins/helper.js';
import BlockForm from '@/components/ContentLibrary/BlockForm.vue';
import * as bootstrap from 'bootstrap';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';

export default ({
   components: {
      BlockForm,
      LoadingPage
   },
   props: {
      organisationId: {},
      blockID: {}
   },
   data() {
      return {
         activeTab: 'contract', // Default active tab
         search: "",
         debounceFn: null,
         blocks: [],
         blockTypes: [],
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "5", value: 5 },
            { text: "10", value: 10 },
            { text: "20", value: 20 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "dateCreated_desc" },
            { text: "Date: Oldest", value: "dateCreated_asc" },
         ],
         sortBy: "dateCreated_desc",
         sorting: {
            field: "dateCreated",
            order: "desc"
         },
         filtering: {
            field: "",
            value: ""
         },
         isLoading: false,
         isModalOpen: false,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingDeleteId: null,
         defaultOnly: true,
      }
   },
   computed: {
      paginationLabel() {
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return `${start} - ${end}`;
      },
      currentPage() { return this.pagination.page; },
      rowsPerPage() { return this.pagination.pageSize; },
      sortItems() { return this.sortBy; },
      filterItems() { return this.filtering.value; },
      currentOrg() { return this.organisationId; }
   },
   watch: {
      currentPage() { this.getBlockList(); },
      rowsPerPage() {
         this.pagination.page = 1;
         this.getBlockList();
      },
      sortItems() {
         if (this.sortBy.includes("_")) {
            let sort = this.sortBy.split("_");
            this.sorting.field = sort[0];
            this.sorting.order = sort[1];
         } else {
            this.sorting.field = this.sortBy;
            this.sorting.order = "";
         }
         this.getBlockList();
      },
      filterItems() {
         this.pagination.page = 1;
         this.getBlockList();
      },
      defaultOnly() {
         this.pagination.page = 1;
         this.getBlockList();
      },
      currentOrg() { this.init(); }
   },
   created() {
      this.init();
      this.debounceFn = debounce(() => this.getBlockList(), 500);
   },
   mounted() {
      if (process.env.NODE_ENV !== undefined && process.env.NODE_ENV == "uat" && this.$store.state.auth.tiny_mce_api_key == "") {
         this.openPopup();
      }
   },
   beforeUnmount() {
   },
   methods: {
      init() {
         this.getBlockList();
         this.getBlockTypes();
      },
      switchTab(tab) {
         if (this.activeTab !== tab) {
            this.activeTab = tab;
            this.pagination.page = 1;
            this.getBlockList();
         }
      },

      formatDate(date) {
         return moment(date).format("DD-MMM-YYYY");
      },
      previousPage() { if (this.pagination.page > 1) { this.pagination.page--; } },
      nextPage() { if (this.pagination.page < this.pagination.totalPages) { this.pagination.page++; } },
      truncateLongText(text, limit) { return truncateLongText(text, limit); },
      onButtonClick(action, id) {
         if (action == "delete") {
            this.deleteBlock(id);
         } else if (action == "block-tab") {
            this.$router.push({ name: 'block-tab-view', params: { id: id } });
         } else if (action == 'create') {
             // Logic handled in template, but you can add specific setup here if needed
         }
      },
      openBlockInNewTab(id) {
         const routeData = this.$router.resolve({ name: 'block-tab-view', params: { id: id } });
         window.open(routeData.href, '_blank');
      },
      async deleteBlock(id) {
         this.pendingDeleteId = id;
         this.confirmModalTitle = 'Delete Block';
         this.confirmModalMessage = 'Do you want to delete this block?';
         this.showConfirmModal = true;
      },
      async onConfirmAction() {
         const id = this.pendingDeleteId;
         this.pendingDeleteId = null;
         if (!id) return;
         await this.$axios.delete("/blocks/" + id)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message);
                  this.getBlockList();
               } else {
                  this.$toast.error(response.data.message);
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message);
            });
      },
      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },
      async getBlockTypes() {
         await this.$axios.get("/categories?name=BlockType")
            .then((response) => {
               this.blockTypes = response.data.category.subcategories;
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message);
            });
      },
      async getBlockList() {
         this.isLoading = true;
         let apiPath = `/blocks?pageSize=${this.pagination.pageSize}&page=${this.pagination.page}`;
         
         if (this.sortBy !== "name_asc") {
            apiPath += `&sort=${this.sorting.field}&order=${this.sorting.order}`;
         }
         if (this.filtering.field && this.filtering.value) {
            apiPath += `&filterBy=${this.filtering.field}&filterValue=${this.filtering.value}`;
         }

         // --- NEW TAB FILTER LOGIC ---
         // Ensure your backend supports filtering by "BlockUse.name" or similar
         if (this.activeTab === 'contract') {
             apiPath += `&filterBy=BlockUse&filterValue=Contract`; 
         } else if (this.activeTab === 'proposal') {
             apiPath += `&filterBy=BlockUse&filterValue=Proposal`;
         }
         // ---------------------------

         if (this.defaultOnly) {
             apiPath += `&defaultOnly=true`;
         }

         if (this.search) {
            apiPath += `&search=${this.search}`;
         }
         await this.$axios.get(apiPath)
            .then((response) => {
               this.blocks = response.data.blocks;
               this.pagination.totalItems = response.data.total_items;
               this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize) || 1;
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message);
            })
            .finally(() => {
                this.isLoading = false;
            });
      },
      openPopup() {
         const tinyMCEModalEl = document.getElementById('popup_tiny_mce_api_key');
         if (tinyMCEModalEl) {
             const tinyMCEModal = new bootstrap.Modal(tinyMCEModalEl);
             tinyMCEModal.show();
         }
      },
      closeModal() {
         this.isModalOpen = false;
         if (this.$refs.blockFormRef) {
             this.$refs.blockFormRef.resetForm();
         }
      },
   },
});
</script>

<style scoped>
/* --- General & Card Styles --- */
.filterTitle {
   font-weight: 500;
   color: #435666;
}
.filterOptions {
   font-weight: 500;
   color: #00A0C1;
}
.bordered-select {
   border: 1px solid #dee2e6 !important;
   border-radius: 8px !important;
   padding-top: 0.25rem;
   padding-bottom: 0.25rem;
   padding-left: 0.75rem;
   width: auto;
}

/* --- Improved Card Styles --- */
.block-card {
   border: 1px solid #e0e0e0;
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
   overflow: hidden; /* Ensures header bar stays within rounded corners */
   cursor: pointer;
}
.block-card:hover {
   transform: translateY(-5px);
   box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}
.card-header-color-bar {
    height: 10px; /* Provides a colored accent without taking too much space */
}
.block-card .card-title {
   font-weight: 700;
   color: #333;
   margin-bottom: 0.5rem;
}
.block-card .card-text {
    font-size: 0.9rem;
    color: #555;
    margin-bottom: 0.5rem;
}
.block-card .card-text strong {
    color: #333;
}
.card-content-area {
    min-height: 120px; /* Ensures a consistent minimum height for content before buttons */
}

/* --- Label Styles --- */
.label-container {
   display: flex;
   flex-wrap: wrap;
   gap: 5px;
   margin-top: 5px;
}
.label {
   background-color: #34495e;
   color: white;
   padding: 10px 10px;
   border: none;
   border-radius: 5px;
   font-weight: bold;
}


/* --- Pagination Styles --- */
.pagination {
    display: inline-flex;
    align-items: center;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;
}
.pagination-label {
    font-size: 0.9rem;
    color: #6c757d;
    font-weight: 500;
    padding: 0 8px;
    white-space: nowrap;
}
.pagination-label strong {
    color: #343a40;
}
.pagination-arrow {
    background-color: transparent;
    border: none;
    cursor: pointer;
    color: #00A0C1;
    font-size: 1rem;
    padding: 8px 12px;
    transition: background-color 0.2s ease;
}
.pagination-arrow:hover:not(:disabled) {
    background-color: #f1f3f5;
}
.pagination-arrow:disabled {
    color: #ced4da;
    cursor: not-allowed;
}

/* --- Action Button Styles --- */
.btn-circle-xl {
   width: 40px;
   height: 40px;
   padding: 0;
   border-radius: 50%;
   text-align: center;
   font-size: 1rem;
   display: inline-flex;
   justify-content: center;
   align-items: center;
}
.btn-circle-info {
   background-color: #e6f6f9;
   color: #00a0c1;
   border: 1px solid #b3e7f2;
}
.btn-circle-info:hover {
   color: #fff;
   background-color: #00a0c1;
}
.btn-circle-dark {
   background-color: #f0f0f0;
   color: #555;
   border: 1px solid #dcdcdc;
}
.btn-circle-dark:hover {
   color: #fff;
   background-color: #424649;
}

/* --- Tab Styles --- */
.nav-tabs .nav-link {
    color: #495057;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-bottom: none;
    margin-right: 2px;
}
.nav-tabs .nav-link:hover {
    border-color: #e9ecef #e9ecef #dee2e6;
}
.nav-tabs .nav-link.active {
    color: #00A0C1;
    background-color: #fff;
    border-color: #dee2e6 #dee2e6 #fff;
}

/* --- Custom BDM Modal Styles --- */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   max-width: 500px;
   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   color: #435666;
   margin-bottom: 0;
}

.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
}

.modal-body-custom {
   width: 100%;
}

/* Custom scrollbar styles removed to prevent unwanted axis rendering */
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
}

.close-button-custom:hover {
   color: #000;
}

/* Custom premium switch styling */
.custom-switch-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}
.custom-switch-input {
   border-color: #dee2e6 !important;
   transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}
.custom-switch-input:hover {
   transform: scale(1.05);
}
.custom-switch-input:focus {
   box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.25) !important;
}
</style>